import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';

// /dashboard is used in emails + OAuth redirects — forward to / KEEPING the query string
function DashboardAlias() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
}
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { useState } from 'react';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SecurityPage from './pages/SecurityPage';
import DataProcessingPolicy from './pages/DataProcessingPolicy';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import CallHistory from './pages/CallHistory';
import CallSummary from './pages/CallSummary';
import TeamManagement from './pages/TeamManagement';
import BillingPage from './pages/BillingPage';
import ManagerDashboard from './pages/ManagerDashboard';
import PlatformDashboard from './pages/PlatformDashboard';
import AdminSetupWizard from './pages/AdminSetupWizard';
import RepSetupWizard from './pages/RepSetupWizard';
import ChangelogPage from './pages/ChangelogPage';
import StatusPage from './pages/StatusPage';
import SubProcessorsPage from './pages/SubProcessorsPage';
import SLAPage from './pages/SLAPage';
import DemoPage from './pages/DemoPage';
import AccountStatus from './pages/AccountStatus';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ProspectResearch from './pages/ProspectResearch';
import { HelpWidget } from './components/HelpWidget';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { CookieConsent } from './components/CookieConsent';
import './App.css';

function TermsConsentGate({ children }) {
  const { user, acceptTerms, logout } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [agreed, setAgreed] = useState(false);

  if (user && user.terms_accepted === false) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-8" data-testid="terms-consent-modal">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Updated Policies</h2>
          <p className="text-sm text-zinc-600 mb-6">
            We&apos;ve updated our Terms of Service and Privacy Policy. Please review and accept them to continue using PipeGuide.
          </p>
          
          <div className="space-y-2 mb-6 text-sm">
            <Link to="/terms" target="_blank" className="block text-brand-primary hover:underline" data-testid="consent-terms-link">
              Terms of Service
            </Link>
            <Link to="/privacy" target="_blank" className="block text-brand-primary hover:underline" data-testid="consent-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/data-processing" target="_blank" className="block text-brand-primary hover:underline" data-testid="consent-data-link">
              Data Processing Policy
            </Link>
          </div>

          <div className="flex items-start gap-2 mb-6">
            <input
              type="checkbox"
              id="consent-agree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 rounded border-zinc-300"
              data-testid="consent-checkbox"
            />
            <label htmlFor="consent-agree" className="text-xs text-zinc-600 leading-relaxed">
              I have read and agree to the Terms of Service, Privacy Policy, and Data Processing Policy. 
              I understand that conversations will be recorded and analyzed by AI.
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={async () => {
                setAccepting(true);
                await acceptTerms();
                setAccepting(false);
              }}
              disabled={!agreed || accepting}
              className="flex-1 bg-brand-primary hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2.5 rounded-md transition-colors"
              data-testid="consent-accept-btn"
            >
              {accepting ? 'Accepting...' : 'Accept & Continue'}
            </button>
            <button
              onClick={logout}
              className="px-4 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md transition-colors"
              data-testid="consent-decline-btn"
            >
              Decline & Log Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <TermsConsentGate>{children}</TermsConsentGate>;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <TermsConsentGate>{children}</TermsConsentGate>;
}

function ManagerRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin' && user.role !== 'manager') {
    return <Navigate to="/" replace />;
  }

  return <TermsConsentGate>{children}</TermsConsentGate>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function RoleBasedHome() {
  const { user } = useAuth();

  // Show setup wizard for first-time users
  // setup_completed === false means explicitly not completed
  // undefined/null means legacy user (before wizard was added) — skip wizard
  // Sales-first trial: admins of orgs with zero completed calls land straight
  // on the Dashboard (Start your first call) — BEFORE the setup wizard.
  // Workspace setup unlocks after the first call (see dropdown hint).
  if (user?.role === 'admin' && user.org_onboarding && !user.org_onboarding.has_completed_first_call) {
    return <Dashboard />;
  }

  if (user && user.setup_completed === false) {
    if (user.role === 'admin') {
      return <Navigate to="/setup" replace />;
    }
    if (user.role === 'sales') {
      return <Navigate to="/setup/rep" replace />;
    }
  }

  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  if (user?.role === 'manager') {
    return <Navigate to="/manager" replace />;
  }
  return <Dashboard />;
}

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id from Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public marketing/info pages — accessible without auth */}
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/account-status" element={<AccountStatus />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/sub-processors" element={<SubProcessorsPage />} />
      <Route path="/sla" element={<SLAPage />} />
      <Route path="/demo" element={<DemoPage />} />

      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/verify-email" 
        element={
          <ProtectedRoute>
            <VerifyEmail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <RoleBasedHome />
          </ProtectedRoute>
        } 
      />
      <Route path="/dashboard" element={<DashboardAlias />} />
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        } 
      />
      <Route 
        path="/team" 
        element={
          <ManagerRoute>
            <TeamManagement />
          </ManagerRoute>
        } 
      />
      <Route 
        path="/manager" 
        element={
          <ManagerRoute>
            <ManagerDashboard />
          </ManagerRoute>
        } 
      />
      <Route 
        path="/billing" 
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <CallHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/research" 
        element={
          <ProtectedRoute>
            <ProspectResearch />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/call/:sessionId" 
        element={
          <ProtectedRoute>
            <CallSummary />
          </ProtectedRoute>
        } 
      />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/data-processing" element={<DataProcessingPolicy />} />
      <Route 
        path="/setup" 
        element={
          <ProtectedRoute>
            <AdminSetupWizard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/setup/rep" 
        element={
          <ProtectedRoute>
            <RepSetupWizard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/platform" 
        element={
          <ProtectedRoute>
            <PlatformDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell() {
  const { user } = useAuth();
  return (
    <>
      <ImpersonationBanner />
      <AppRoutes />
      {/* Help widget — only render for authenticated users */}
      {user && <HelpWidget />}
      <CookieConsent />
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
